const API_URL = process.env.REACT_APP_API_URL || '';

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v';

export interface Course {
  id: number;
  fullname: string;
  shortname: string;
  startdate: Date | null;
  enddate: Date | null;
  status: 'En curso' | 'Finalizado';
  total_students: number;
  approved_students: number;
}

export interface Student {
  moodle_id: number;
  fullname: string;
  firstname: string;
  lastname: string;
  email: string;
  document_type: string;
  document_number: string;
  percentage: number;
  is_approved: boolean;
  grade: string;
}

export interface CourseStats {
  total_courses: number;
  total_students: number;
  total_approved: number;
  courses_in_progress: number;
  courses_completed: number;
}

export interface CourseFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  startDateFrom?: string;
  startDateTo?: string;
  minStudents?: string;
  maxStudents?: string;
}

class CampusService {
  // OBTENER TOKEN DE LA MISMA FORMA QUE EN AuthHelpers
  private getToken(): string | null {
    if (!localStorage) {
      return null;
    }

    const lsValue: string | null = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
    if (!lsValue) {
      console.warn('[CampusService] No hay token en localStorage');
      return null;
    }

    try {
      const auth: { api_token: string } = JSON.parse(lsValue);
      if (auth && auth.api_token) {
        console.log('[CampusService] Token encontrado correctamente');
        return auth.api_token;
      }
    } catch (error) {
      console.error('[CampusService] Error parsing auth:', error);
    }

    return null;
  }

  private getBaseUrl(): string {
    return API_URL;
  }

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const baseUrl = this.getBaseUrl();
    
    if (!token) {
      console.error('[CampusService] No hay token disponible');
      throw new Error('No autenticado. Por favor, inicia sesión nuevamente.');
    }
    
    console.log('[CampusService] Haciendo request a:', `${baseUrl}${url}`);
    
    const response = await fetch(`${baseUrl}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    });

    if (!response.ok) {
      // Si es 401, el token expiró
      if (response.status === 401) {
        // Limpiar token y redirigir al login
        localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
        window.location.href = '/login';
        throw new Error('Sesión expirada. Por favor, inicia sesión nuevamente.');
      }
      
      const error = await response.json().catch(() => ({}));
      console.error('[CampusService] Error response:', response.status, error);
      throw new Error(error.message || `Error ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  async getCourses(filters: CourseFilters = {}): Promise<{ courses: Course[]; stats: CourseStats }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    
    return this.request<{ courses: Course[]; stats: CourseStats }>(
      `/campus/courses?${params.toString()}`
    );
  }

  async getCourseStudents(courseId: number): Promise<{ students: Student[]; stats: any }> {
    return this.request<{ students: Student[]; stats: any }>(
      `/campus/course/${courseId}/students`
    );
  }

  async generateCertificate(studentId: number, courseId: number): Promise<Blob> {
    const token = this.getToken();
    const baseUrl = this.getBaseUrl();
    
    if (!token) {
      throw new Error('No autenticado. Por favor, inicia sesión nuevamente.');
    }
    
    const response = await fetch(`${baseUrl}/campus/certificate/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ studentId, courseId })
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
        window.location.href = '/login';
        throw new Error('Sesión expirada.');
      }
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || `Error ${response.status}`);
    }

    return response.blob();
  }

  async sendCertificate(studentId: number, courseId: number, email?: string): Promise<any> {
    return this.request('/campus/certificate/send', {
      method: 'POST',
      body: JSON.stringify({ studentId, courseId, email })
    });
  }

  async sendBatchCertificates(courseId: number, studentIds: number[]): Promise<any> {
    return this.request('/campus/certificate/send-batch', {
      method: 'POST',
      body: JSON.stringify({ courseId, studentIds })
    });
  }
}

export default new CampusService();